import api from '../api';
import i18n from '../i18n';

// Static images shown until the CMS provides its own; the same files the API falls back to.
const DEFAULT_IMAGE = {
  large: '/home-background.jpg',
  medium: '/home-background-small.jpg',
};

let pending = null;
const albumRequests = {};

// Members and boards share one loading pattern: one request at a time per resource,
// none once the data is in, and a retry after a failure.
const requests = {};
const emptyResource = () => ({ data: null, loading: false, error: false });
// An album is fetched per slug; an unknown or unpublished one answers 404, which is not a
// failure to retry.
const emptyAlbumPage = () => ({
  data: null,
  loading: false,
  error: false,
  notFound: false,
});

function loadResource(name, request, state, commit) {
  if (state[name].data) {
    return Promise.resolve();
  }

  if (requests[name]) {
    return requests[name];
  }

  commit('SET_RESOURCE', { name, patch: { loading: true, error: false } });
  requests[name] = request()
    .then(data => commit('SET_RESOURCE', { name, patch: { data, loading: false } }))
    .catch(() => commit('SET_RESOURCE', { name, patch: { loading: false, error: true } }))
    .then(() => {
      requests[name] = null;
    });

  return requests[name];
}

export default {
  namespaced: true,
  state: {
    site: null,
    loaded: false,
    loading: false,
    error: false,
    members: emptyResource(),
    boards: emptyResource(),
    albums: emptyResource(),
    albumPages: {},
  },
  getters: {
    albumPage: state => slug => state.albumPages[slug] || emptyAlbumPage(),

    // The CMS data, or the built-in copy while it loads or when it cannot be loaded.
    hero(state) {
      const hero = state.site ? state.site.hero : null;
      const image = hero ? hero.image : null;

      return {
        headline: hero ? hero.headline : i18n.t('home.title'),
        subheadline: hero ? hero.subheadline : i18n.t('home.motto'),
        image: {
          large: image ? image.large : DEFAULT_IMAGE.large,
          medium: image ? image.medium : DEFAULT_IMAGE.medium,
          alt: image ? image.alt : i18n.t('home.hero_alt'),
        },
        // The looping video behind the photo: { src, width, height }, or null without one.
        video: hero && hero.video ? hero.video : null,
      };
    },
  },
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    SET_SITE(state, site) {
      state.site = site;
      state.loaded = true;
      state.error = false;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    // The data Laravel rendered into the page (window.__INTRO__): the first load needs no request.
    HYDRATE(state, preloaded) {
      if (preloaded.site) {
        state.site = preloaded.site;
        state.loaded = true;
        state.error = false;
      }

      ['members', 'boards', 'albums'].forEach((name) => {
        if (preloaded[name]) {
          state[name] = Object.assign({}, state[name], {
            data: preloaded[name],
            loading: false,
            error: false,
          });
        }
      });

      if (preloaded.album) {
        state.albumPages = Object.assign({}, state.albumPages, {
          [preloaded.album.slug]: Object.assign(emptyAlbumPage(), { data: preloaded.album.data }),
        });
      }
    },

    SET_RESOURCE(state, { name, patch }) {
      state[name] = Object.assign({}, state[name], patch);
    },

    SET_ALBUM_PAGE(state, { slug, patch }) {
      state.albumPages = Object.assign({}, state.albumPages, {
        [slug]: Object.assign(emptyAlbumPage(), state.albumPages[slug], patch),
      });
    },
  },
  actions: {
    // One request at a time, and none once the data is in.
    fetchSite({ state, commit }) {
      if (state.loaded) {
        return Promise.resolve();
      }

      if (pending) {
        return pending;
      }

      commit('SET_LOADING', true);
      pending = api.fetchSite()
        .then(site => commit('SET_SITE', site))
        .catch(() => commit('SET_ERROR', true))
        .then(() => {
          pending = null;
          commit('SET_LOADING', false);
        });

      return pending;
    },

    fetchMembers({ state, commit }) {
      return loadResource('members', api.fetchMembers, state, commit);
    },

    fetchBoards({ state, commit }) {
      return loadResource('boards', api.fetchBoards, state, commit);
    },

    fetchAlbums({ state, commit }) {
      return loadResource('albums', api.fetchAlbums, state, commit);
    },

    // Same pattern as loadResource, per slug.
    fetchAlbum({ state, commit }, slug) {
      if (state.albumPages[slug] && state.albumPages[slug].data) {
        return Promise.resolve();
      }

      if (albumRequests[slug]) {
        return albumRequests[slug];
      }

      commit('SET_ALBUM_PAGE', { slug, patch: { loading: true, error: false, notFound: false } });
      albumRequests[slug] = api.fetchAlbum(slug)
        .then(data => commit('SET_ALBUM_PAGE', { slug, patch: { data, loading: false } }))
        .catch(error => commit('SET_ALBUM_PAGE', {
          slug,
          patch: { loading: false, error: error.status !== 404, notFound: error.status === 404 },
        }))
        .then(() => {
          albumRequests[slug] = null;
        });

      return albumRequests[slug];
    },
  },
};
